import { v4 as uuidv4 } from 'uuid';
import { SentryLogger } from '../../sentryLogging/SentryLogger';
import * as Sentry from '@sentry/browser';
import { getErrorObject } from '../Errors';
import { ResponseStatus } from '../Constants';
import { ErrorJSON, ResponseJSON, ResultJSON } from 'src/Common/BridgeInterface';
import { MessageFromNative } from '../core/TypeDefinitions';

class MessageToNative {
	readonly id: string;
	readonly resolve: Function;
	readonly reject: Function;

	constructor(resolve: Function, reject?: Function) {
		this.id = uuidv4();
		this.resolve = resolve;
		this.reject = reject;
	}
}

export class NativeBridgeMapper {
    private requestMap: Map<string, MessageToNative> = new Map();
	private functionMap: { [key: string]: Function };

    public setFunctionMap(functionMap: { [key: string]: Function }) {
        this.functionMap = functionMap;
    }

	protected addRequestToQueue(resolve: Function, reject?: Function): string {
		const messageToNative = new MessageToNative(resolve, reject);
		this.requestMap.set(messageToNative.id, messageToNative);
		return messageToNative.id;
	}

	public onMessageReceived<T extends ResponseJSON>(message: MessageFromNative<T>): void {
		switch (message.type) {
			case 'call': {
				const { id, name, params } = message;
				this.functionMap[name].call(this, { id, params });
				break;
			}
			case 'callback': {
				const { id, params: response } = message;
				if (this.isError(response)) {
					this.rejectPromise(id, response.payload as ErrorJSON);
					return;
				}
				this.resolvePromise(id, response.payload);
				break;
			}
			case 'event': {
				const { name, params } = message;
				this.functionMap[name].call(this, { id: undefined, params });
				break;
			}
			default: {
				const msgType = (message as MessageFromNative<T>).type;
				SentryLogger.getInstance().addBreadCrumb({
					category: 'bridge',
					message: `Received unknown type of message: ${msgType}`,
					level: Sentry.Severity.Error,
				});
				console.log(`unknown type of message: ${msgType}`);
				break;
			}
		}
	}

    private resolvePromise(id: string, value: ResultJSON): void {
		const req = this.requestMap.get(id);
		if (req) {
			this.requestMap.delete(id);
			req.resolve(value);
			return;
		}
		SentryLogger.getInstance().addBreadCrumb({
			category: 'accountConfig',
			message: `Could not retrieve promise with id ${id} from map to resolve promise`,
			level: Sentry.Severity.Error,
		});
	}

	private rejectPromise(id: string, value: ErrorJSON): void {
		const req = this.requestMap.get(id);
		const { errorCode, errorDescription, payload } = value;
		if (req) {
			this.requestMap.delete(id);
			const errorObj = getErrorObject(errorCode, errorDescription, payload);
			req.reject(errorObj);
			return;
		}
		SentryLogger.getInstance().addBreadCrumb({
			category: 'accountConfig',
			message: `Could not retrieve promise with id ${id} from map to reject promise`,
			level: Sentry.Severity.Error,
		});
	}

	private isError<T extends ResponseJSON>(response: T): boolean {
		return response && response.status === ResponseStatus.Failure ? true : false;
	}

}
